/**
 * Elafiya Shopify Theme - Quick Add Modal
 * ========================================
 * Variant selection modal triggered from product cards.
 * Fetches product data via /products/{handle}.js, lets user
 * pick a variant, then adds to cart via the global cartDrawer.
 *
 * No jQuery or framework dependencies.
 */

(function () {
  'use strict';

  /* =========================================================================
     QUICK ADD MODAL CLASS
     ========================================================================= */

  class QuickAddModal {
    constructor() {
      this.modal = document.getElementById('quick-add-modal');
      this.content = document.getElementById('quick-add-modal-content');
      this.atcBtn = document.getElementById('quick-add-atc');

      if (!this.modal || !this.content || !this.atcBtn) return;

      this.product = null;
      this.variants = [];
      this.optionNames = [];
      this.selectedOptions = {};
      this.currentVariant = null;

      this._bindEvents();
    }

    /* ---- Open / Close ---------------------------------------------------- */

    /**
     * Open the modal for a given product handle.
     * Fetches product data from the Shopify AJAX API.
     *
     * @param {string} handle - Product handle (slug).
     */
    async open(handle) {
      if (!handle) return;

      // Show modal with loading state
      this.content.innerHTML =
        '<div class="quick-add-modal__loading"><div class="quick-add-modal__spinner"></div></div>';
      this.atcBtn.disabled = true;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('quick-add-open');
      /* Freeze the page behind the modal. `no-scroll` is the theme's shared
         lock (theme.css:69 + the observer in theme.js): position:fixed, which
         iOS needs — overflow:hidden alone does nothing there — plus save and
         restore of the scroll offset. */
      document.body.classList.add('no-scroll');
      if (window.trapFocus) {
        if (this._releaseTrap) this._releaseTrap();
        this._releaseTrap = window.trapFocus(this.modal);
      }

      try {
        // Use locale-aware root path so Translate & Adapt returns translated data
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var res = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js');
        if (!res.ok) throw new Error('Product not found');
        this.product = await res.json();
      } catch (err) {
        console.error('[QuickAddModal] Failed to fetch product:', err);
        this.content.innerHTML =
          '<div style="text-align:center;padding:2rem;color:#b91c1c;">Could not load product. Please try again.</div>';
        return;
      }

      this.variants = this.product.variants || [];
      this.optionNames = this._getOptionNames();
      this._initOptions();
      this._render();
    }

    close() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('quick-add-open');
      /* Always release. Adding to cart opens the drawer, which applies its own
         lock — but this class is ours, and skipping the removal while the
         drawer was up left it on the body forever: the drawer's close() clears
         its own inline styles and the cart-drawer-open class, yet no-scroll
         still matched body.no-scroll{position:fixed} and the page stayed
         frozen. The scroll-lock observer knows not to disturb the drawer's
         offset when it sees this go away. */
      document.body.classList.remove('no-scroll');
      if (this._releaseTrap) {
        this._releaseTrap();
        this._releaseTrap = null;
      }
      this.product = null;
      this.variants = [];
      this.optionNames = [];
      this.selectedOptions = {};
      this.currentVariant = null;
    }

    /* ---- Option / Variant Logic ------------------------------------------ */

    /**
     * Normalise product.options to an array of name strings.
     * Shopify /products/{handle}.js can return either plain strings
     * ["Size","Color"] or objects [{name:"Size", position:1, values:[…]}].
     * @returns {string[]}
     * @private
     */
    _getOptionNames() {
      var options = this.product.options || [];
      return options.map(function (opt) {
        return typeof opt === 'object' && opt !== null ? opt.name : String(opt);
      });
    }

    /**
     * Initialise selectedOptions from the first variant.
     * @private
     */
    _initOptions() {
      this.selectedOptions = {};
      var names = this.optionNames;

      // product.variants[0].option1, option2, option3 hold the values
      if (this.variants.length > 0) {
        var firstVariant = this.variants[0];
        for (var i = 0; i < names.length; i++) {
          this.selectedOptions[names[i]] = firstVariant['option' + (i + 1)];
        }
      }

      this.currentVariant = this.variants[0] || null;
    }

    /**
     * Find the variant matching current selectedOptions.
     * @returns {Object|null}
     * @private
     */
    _findVariant() {
      var self = this;
      var names = this.optionNames;

      return this.variants.find(function (v) {
        return names.every(function (optName, idx) {
          return v['option' + (idx + 1)] === self.selectedOptions[optName];
        });
      }) || this.variants[0] || null;
    }

    /**
     * Handle option change from a dropdown.
     * @param {string} optionName
     * @param {string} value
     * @private
     */
    _onOptionChange(optionName, value) {
      this.selectedOptions[optionName] = value;
      this.currentVariant = this._findVariant();
      this._updateUI();
    }

    /* ---- Rendering ------------------------------------------------------- */

    /**
     * Build the full modal content after product data is loaded.
     * @private
     */
    _render() {
      var product = this.product;
      var variant = this.currentVariant;
      if (!product || !variant) return;

      var html = '';

      // Product info row
      html += '<div class="quick-add-modal__product">';
      html += '  <img id="qa-modal-image" class="quick-add-modal__image" src="' + this._getVariantImage(variant) + '" alt="' + _escapeHtml(product.title) + '" width="80" height="80">';
      html += '  <div class="quick-add-modal__product-info">';
      html += '    <h3 id="qa-modal-title" class="quick-add-modal__product-title">' + _escapeHtml(this._getDisplayTitle(variant)) + '</h3>';
      if (product.vendor) {
        html += '    <p class="quick-add-modal__product-vendor">' + _escapeHtml(product.vendor) + '</p>';
      }
      html += '    <div id="qa-modal-variant-label" class="quick-add-modal__variant-label" style="' + (variant.title === 'Default Title' ? 'display:none' : '') + '">' + _escapeHtml(variant.title) + '</div>';
      html += '    <div id="qa-modal-price" class="quick-add-modal__price">';
      html += this._renderPrice(variant);
      html += '    </div>';
      html += '  </div>';
      html += '</div>';

      // Variant options
      var names = this.optionNames;
      var optionValuesMap = this._getOptionValuesMap();

      if (names.length > 0 && this.variants.length > 1) {
        html += '<div class="quick-add-modal__options">';
        html += '<p class="quick-add-modal__options-heading">' + _t('options', 'Options') + '</p>';

        for (var i = 0; i < names.length; i++) {
          var optName = names[i];
          var values = optionValuesMap[optName] || [];
          if (values.length <= 1) continue;

          html += '<div class="quick-add-modal__option-group">';
          html += '  <label class="quick-add-modal__option-label">' + _escapeHtml(optName) + '</label>';
          html += '  <select class="quick-add-modal__option-select" data-option-name="' + _escapeHtml(optName) + '">';

          for (var j = 0; j < values.length; j++) {
            var val = values[j];
            var selected = this.selectedOptions[optName] === val ? ' selected' : '';

            // Find price for this option value
            var priceLabel = this._getPriceForOptionValue(optName, i, val);

            // Use Unicode LTR Isolate (\u2066...\u2069) around price to prevent
            // RTL bidi from merging variant number with price (e.g. "1" + "$23.99" → "123.99$")
            html += '<option value="' + _escapeHtml(val) + '"' + selected + '>'
              + '\u2068' + _escapeHtml(val) + '\u2069'
              + (priceLabel ? ' \u2014 \u2066' + priceLabel + '\u2069' : '')
              + '</option>';
          }

          html += '  </select>';
          html += '</div>';
        }

        html += '</div>';
      }

      // Direct variant fallback (no structured options but multiple variants)
      if (this.variants.length > 1 && (names.length === 0 || !this._hasMultiValueOptions(optionValuesMap))) {
        html += '<div class="quick-add-modal__option-group">';
        html += '<label class="quick-add-modal__option-label">' + _t('selectVariant', 'Select Variant') + '</label>';
        html += '<select class="quick-add-modal__option-select" data-variant-direct>';

        for (var k = 0; k < this.variants.length; k++) {
          var v = this.variants[k];
          var sel = v.id === (this.currentVariant && this.currentVariant.id) ? ' selected' : '';
          html += '<option value="' + v.id + '"' + sel + '>'
            + '\u2068' + _escapeHtml(v.title) + '\u2069'
            + ' \u2014 \u2066' + Shopify.formatMoney(v.price) + '\u2069'
            + '</option>';
        }

        html += '</select>';
        html += '</div>';
      }

      // Out of stock warning
      html += '<div id="qa-modal-oos" class="quick-add-modal__out-of-stock" style="' + (variant.available ? 'display:none' : '') + '">';
      html += '  <p>' + _t('variantUnavailable', 'This variant is currently out of stock') + '</p>';
      html += '</div>';

      this.content.innerHTML = html;

      // Bind dropdown change events
      this._bindOptionSelects();

      // Update ATC button state
      this._updateATCButton();
    }

    /**
     * Update the dynamic parts of the modal without rebuilding everything.
     * @private
     */
    _updateUI() {
      var variant = this.currentVariant;
      if (!variant) return;

      // Image
      var imgEl = document.getElementById('qa-modal-image');
      if (imgEl) {
        imgEl.style.opacity = '0.5';
        imgEl.src = this._getVariantImage(variant);
        imgEl.onload = function () { imgEl.style.opacity = '1'; };
      }

      // Title
      var titleEl = document.getElementById('qa-modal-title');
      if (titleEl) titleEl.textContent = this._getDisplayTitle(variant);

      // Variant label
      var labelEl = document.getElementById('qa-modal-variant-label');
      if (labelEl) {
        if (variant.title && variant.title !== 'Default Title') {
          labelEl.textContent = variant.title;
          labelEl.style.display = '';
        } else {
          labelEl.style.display = 'none';
        }
      }

      // Price
      var priceEl = document.getElementById('qa-modal-price');
      if (priceEl) priceEl.innerHTML = this._renderPrice(variant);

      // Out of stock
      var oosEl = document.getElementById('qa-modal-oos');
      if (oosEl) oosEl.style.display = variant.available ? 'none' : '';

      // ATC button
      this._updateATCButton();
    }

    /**
     * @private
     */
    _updateATCButton() {
      if (!this.atcBtn || !this.currentVariant) return;

      if (this.currentVariant.available) {
        this.atcBtn.disabled = false;
        this.atcBtn.textContent = _t('addToCart', 'Add to Cart');
        this.atcBtn.className = 'quick-add-modal__add-to-cart';
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = _t('soldOut', 'Sold Out');
        this.atcBtn.className = 'quick-add-modal__add-to-cart';
      }
    }

    /* ---- Helpers ---------------------------------------------------------- */

    /**
     * Get the best image for a variant.
     * @private
     */
    _getVariantImage(variant) {
      // Variant's own featured image
      if (variant.featured_image && variant.featured_image.src) {
        return variant.featured_image.src;
      }

      // Try to match variant to product images by index
      if (this.product.images && this.product.images.length > 0) {
        var variantIndex = this.variants.indexOf(variant);
        if (variantIndex >= 0 && this.product.images[variantIndex]) {
          return this.product.images[variantIndex];
        }
        // Fallback to first image
        return this.product.images[0];
      }

      return '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
    }

    /**
     * Build display title with variant suffix.
     * @private
     */
    _getDisplayTitle(variant) {
      var title = this.product.title;
      if (variant && variant.title && variant.title !== 'Default Title' && variant.title !== title) {
        return title + ' - ' + variant.title;
      }
      return title;
    }

    /**
     * Render price HTML for a variant.
     * @private
     */
    _renderPrice(variant) {
      if (!variant || !variant.price) return '';
      var html = '<span class="quick-add-modal__price-current">' + Shopify.formatMoney(variant.price) + '</span>';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += '<span class="quick-add-modal__price-compare">' + Shopify.formatMoney(variant.compare_at_price) + '</span>';
        var pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
        html += '<span class="quick-add-modal__price-badge">-' + pct + '%</span>';
      }
      return html;
    }

    /**
     * Build a map of { optionName: [uniqueValues] }.
     * @private
     */
    _getOptionValuesMap() {
      var map = {};
      var names = this.optionNames;

      for (var i = 0; i < names.length; i++) {
        var optName = names[i];
        var seen = [];

        for (var j = 0; j < this.variants.length; j++) {
          var val = this.variants[j]['option' + (i + 1)];
          if (val && seen.indexOf(val) === -1) {
            seen.push(val);
          }
        }

        map[optName] = seen;
      }

      return map;
    }

    /**
     * Check if any option has more than 1 value.
     * @private
     */
    _hasMultiValueOptions(map) {
      var keys = Object.keys(map);
      for (var i = 0; i < keys.length; i++) {
        if (map[keys[i]].length > 1) return true;
      }
      return false;
    }

    /**
     * Get formatted price for a specific option value.
     * Finds the variant that matches current selection + the given value.
     * @private
     */
    _getPriceForOptionValue(optionName, optionIndex, value) {
      var testOptions = {};
      for (var key in this.selectedOptions) {
        testOptions[key] = this.selectedOptions[key];
      }
      testOptions[optionName] = value;

      var names = this.optionNames;
      var match = this.variants.find(function (v) {
        return names.every(function (name, idx) {
          return v['option' + (idx + 1)] === testOptions[name];
        });
      });

      if (match && match.price) {
        return Shopify.formatMoney(match.price);
      }
      return '';
    }

    /* ---- Event Binding --------------------------------------------------- */

    /**
     * Bind all persistent event listeners.
     * @private
     */
    _bindEvents() {
      var self = this;

      // Close buttons (backdrop + X + cancel)
      document.querySelectorAll('[data-quick-add-close]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          self.close();
        });
      });

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.modal.classList.contains('is-open')) {
          self.close();
        }
      });

      // Delegated click: open modal from product cards
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-quick-add-trigger]');
        if (!trigger) return;

        e.preventDefault();
        e.stopPropagation();

        var handle = trigger.dataset.productHandle;
        if (handle) self.open(handle);
      });

      // Add to cart button
      this.atcBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self._addToCart();
      });
    }

    /**
     * Bind change listeners on option selects (called after _render).
     * @private
     */
    _bindOptionSelects() {
      var self = this;

      // Structured option dropdowns
      this.content.querySelectorAll('[data-option-name]').forEach(function (select) {
        select.addEventListener('change', function () {
          self._onOptionChange(select.dataset.optionName, select.value);
        });
      });

      // Direct variant dropdown (fallback)
      var directSelect = this.content.querySelector('[data-variant-direct]');
      if (directSelect) {
        directSelect.addEventListener('change', function () {
          var variantId = parseInt(directSelect.value, 10);
          self.currentVariant = self.variants.find(function (v) { return v.id === variantId; }) || self.variants[0];
          self._updateUI();
        });
      }
    }

    /**
     * Add the selected variant to cart.
     * @private
     */
    async _addToCart() {
      if (!this.currentVariant || !this.currentVariant.available) return;

      var btn = this.atcBtn;
      var originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = _t('adding', 'Adding...');

      try {
        // Use the global cartDrawer if available
        if (window.cartDrawer && typeof window.cartDrawer.addItem === 'function') {
          await window.cartDrawer.addItem(this.currentVariant.id, 1);
        } else {
          // Fallback: direct AJAX (locale-aware root, same as the product fetch)
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          var res = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: this.currentVariant.id, quantity: 1 })
          });
          if (!res.ok) throw new Error('Failed to add to cart');
        }

        btn.textContent = _t('added', 'Added!');
        btn.className = 'quick-add-modal__add-to-cart is-success';

        // Close modal after brief success feedback
        var self = this;
        setTimeout(function () {
          self.close();
          btn.textContent = originalText;
          btn.className = 'quick-add-modal__add-to-cart';
          btn.disabled = false;
        }, 1000);

      } catch (err) {
        console.error('[QuickAddModal] Add to cart error:', err);
        btn.textContent = _t('tryAgain', 'Try Again');
        btn.className = 'quick-add-modal__add-to-cart is-error';
        setTimeout(function () {
          btn.textContent = originalText;
          btn.className = 'quick-add-modal__add-to-cart';
          btn.disabled = false;
        }, 2000);
      }
    }
  }


  /* =========================================================================
     HELPER FUNCTIONS
     ========================================================================= */

  /**
   * Escape HTML special characters.
   * @param {string} str
   * @returns {string}
   */
  function _escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /**
   * Simple translation helper.
   * Reads from window.__qaTranslations if available (set in Liquid).
   *
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  function _t(key, fallback) {
    if (window.__qaTranslations && window.__qaTranslations[key]) {
      return window.__qaTranslations[key];
    }
    return fallback;
  }


  /* =========================================================================
     INITIALISATION
     ========================================================================= */

  document.addEventListener('DOMContentLoaded', function () {
    window.quickAddModal = new QuickAddModal();
    console.log('[Elafiya] Quick Add Modal JS initialised.');
  });

})();
